import { useQuery } from "@tanstack/react-query";
import { parseAbi, type Address } from "viem";
import { redesActivas, type RedKeel } from "../config/networks";
import { clientePublico } from "../lib/cliente";
import { EXPLORADOR, urlArkham } from "../lib/enlaces";
import { useT } from "../lib/i18n";
import { Copiar } from "./Copiar";
import { IconoRed } from "./Monedas";

/**
 * Transparencia: todo lo necesario para auditar Keellabs sin fiarse de nosotros.
 *
 * DOS REGLAS que no se rompen aquí:
 *  1. Lo que se puede leer de la cadena SE LEE EN VIVO (factory, lógica, timelock, comisión,
 *     tesorería, operador). Una tabla escrita a mano se queda vieja en silencio el día que cambia el
 *     gobierno, y esta pantalla existe precisamente para que eso no pueda pasar sin que se vea.
 *  2. Cada afirmación del texto sale del código de los contratos (`src/KeelVault.sol`,
 *     `KeelRebalancer.sol`, `KeelVaultFactory.sol`, `KeelTimelock.sol`, `KeelReferidos.sol`). Si
 *     cambia el contrato, hay que revisar este texto: decir algo que el contrato no garantiza es
 *     peor que no decir nada.
 *
 * El código del keeper NO se publica (decisión 2026-10-01); se documenta lo que hace y, sobre todo,
 * lo que el contrato le impide hacer, que es lo que de verdad protege al usuario.
 */

const ABI = parseAbi([
  "function admin() view returns (address)",
  "function implementation() view returns (address)",
  "function rebalancer() view returns (address)",
  "function treasury() view returns (address)",
  "function defaultOperator() view returns (address)",
  "function perfFeeBps() view returns (uint16)",
  "function MAX_PERF_FEE_BPS() view returns (uint16)",
  "function vaultsCount() view returns (uint256)",
  "function owner() view returns (address)",
  "function guardian() view returns (address)",
  "function delay() view returns (uint256)",
  "function gracia() view returns (uint256)",
  "function referidos() view returns (address)",
  "function CONVERT_FEE_BPS() view returns (uint16)",
]);

type Ficha = {
  factory: Address;
  impl: Address;
  logica: Address;
  tesoreria: Address;
  operador: Address;
  feeBps: number;
  feeMaxBps: number;
  vaults: number;
  timelock: Address;
  duenoTimelock?: Address;
  guardian?: Address;
  delay?: number;
  gracia?: number;
  referidos?: Address;
  conversionBps?: number;
};

async function leerRed(red: RedKeel): Promise<Ficha> {
  const c = clientePublico(red.idCadena);
  const f = { address: red.factory, abi: ABI } as const;
  const [admin, impl, logica, tesoreria, operador, fee, feeMax, n] = await c.multicall({
    allowFailure: false,
    contracts: [
      { ...f, functionName: "admin" },
      { ...f, functionName: "implementation" },
      { ...f, functionName: "rebalancer" },
      { ...f, functionName: "treasury" },
      { ...f, functionName: "defaultOperator" },
      { ...f, functionName: "perfFeeBps" },
      { ...f, functionName: "MAX_PERF_FEE_BPS" },
      { ...f, functionName: "vaultsCount" },
    ],
  });
  const t = { address: admin, abi: ABI } as const;
  const l = { address: logica, abi: ABI } as const;
  // Lo del timelock y la lógica va aparte y con fallos permitidos: si el admin no fuera un timelock,
  // la ficha tiene que decirlo (sale vacío) en vez de romper la red entera.
  const [dueno, guardian, delay, gracia, referidos, conv] = await c.multicall({
    allowFailure: true,
    contracts: [
      { ...t, functionName: "owner" },
      { ...t, functionName: "guardian" },
      { ...t, functionName: "delay" },
      { ...t, functionName: "gracia" },
      { ...l, functionName: "referidos" },
      { ...l, functionName: "CONVERT_FEE_BPS" },
    ],
  });
  const ok = <T,>(r: { status: string; result?: unknown }) => (r.status === "success" ? (r.result as T) : undefined);
  return {
    factory: red.factory,
    impl,
    logica,
    tesoreria,
    operador,
    feeBps: Number(fee),
    feeMaxBps: Number(feeMax),
    vaults: Number(n),
    timelock: admin,
    duenoTimelock: ok<Address>(dueno),
    guardian: ok<Address>(guardian),
    delay: ok<bigint>(delay) !== undefined ? Number(ok<bigint>(delay)) : undefined,
    gracia: ok<bigint>(gracia) !== undefined ? Number(ok<bigint>(gracia)) : undefined,
    referidos: ok<Address>(referidos),
    conversionBps: ok<number>(conv) !== undefined ? Number(ok<number>(conv)) : undefined,
  };
}

const pct = (bps: number) => `${(bps / 100).toLocaleString(undefined, { maximumFractionDigits: 2 })}%`;
const horas = (s: number) => `${Math.round(s / 3600)} h`;
const dias = (s: number) => `${Math.round(s / 86400)}`;

function Dir({ red, a, codigo }: { red: RedKeel; a?: Address; codigo?: boolean }) {
  if (!a) return <span className="mut">—</span>;
  const base = EXPLORADOR[red.clave];
  const sufijo = codigo ? (base.includes("blockscout") ? "?tab=contract" : "#code") : "";
  return (
    <span className="dir-auditable">
      <a className="mono" href={`${base}/address/${a}${sufijo}`} target="_blank" rel="noreferrer">
        {a.slice(0, 6)}…{a.slice(-4)}
      </a>
      <Copiar texto={a} />
      <a className="arkham" href={urlArkham(a)} target="_blank" rel="noreferrer" title="Arkham">Arkham ↗</a>
    </span>
  );
}

function FichaRed({ red }: { red: RedKeel }) {
  const t = useT();
  const { data: d, isLoading, isError, refetch } = useQuery({
    queryKey: ["transparencia", red.clave],
    queryFn: () => leerRed(red),
    staleTime: 60_000,
    retry: 2,
  });

  const filas: Array<[string, React.ReactNode, string?]> = d
    ? [
        [t("Factory (crea los vaults nuevos)"), <Dir red={red} a={d.factory} codigo />, t("{n} vaults creados en esta factory", { n: d.vaults })],
        [t("Lógica (rebalanceador)"), <Dir red={red} a={d.logica} codigo />, t("el código que ejecutan los vaults nuevos")],
        [t("Implementación del vault"), <Dir red={red} a={d.impl} codigo />, t("cada vault es un clon de este contrato")],
        ...(d.delay !== undefined
          ? [
              [t("Gobierno (timelock)"), <Dir red={red} a={d.timelock} codigo />,
                t("todo cambio se anuncia {h} antes de aplicarse; caduca a los {d} días", { h: horas(d.delay), d: dias(d.gracia ?? 0) })],
              [t("Dueño del timelock"), <Dir red={red} a={d.duenoTimelock} />, t("la única cuenta que puede anunciar y ejecutar cambios")],
              [t("Guardián"), <Dir red={red} a={d.guardian} />, t("solo puede CANCELAR un cambio anunciado, nunca proponerlo")],
            ] as Array<[string, React.ReactNode, string]>
          : [
              [t("Gobierno (control directo)"), <Dir red={red} a={d.timelock} />,
                t("sin retardo por ahora: los cambios los firma esta wallet fría (hardware) del equipo. El timelock de 24 h se reactivará cuando el contrato esté estable.")],
            ] as Array<[string, React.ReactNode, string]>),
        [t("Robot (operador por defecto)"), <Dir red={red} a={d.operador} />, t("solo reajusta y cobra comisiones; no puede retirar")],
        [t("Tesorería"), <Dir red={red} a={d.tesoreria} />, t("recibe la comisión de Keellabs")],
        [t("Comisión de rendimiento"), <strong>{pct(d.feeBps)}</strong>, t("de las comisiones ganadas, nunca del capital. Tope en el contrato: {m}", { m: pct(d.feeMaxBps) })],
        ...(d.conversionBps !== undefined
          ? [[t("Comisión de conversión"), <strong>{pct(d.conversionBps)}</strong>, t("solo si al retirar eliges recibirlo todo en el estable")] as [string, React.ReactNode, string]]
          : []),
        [t("Registro de referidos"), <Dir red={red} a={d.referidos} codigo />, t("el reparto sale de nuestra comisión, no de la tuya")],
      ]
    : [];

  return (
    <details className="bloque ficha-red">
      <summary>
        <IconoRed clave={red.clave} /> <span>{red.etiqueta}</span>
        {d && <span className="opcional">{t("{n} vaults", { n: d.vaults })} · {pct(d.feeBps)}</span>}
      </summary>
      <div className="cuerpo">
        {isLoading && <p className="detalle">{t("Leyendo la cadena…")}</p>}
        {isError && (
          <p className="detalle">
            {t("No se pudo leer esta red ahora mismo.")}{" "}
            <button className="btn fantasma" onClick={() => refetch()}>{t("Reintentar")}</button>
          </p>
        )}
        {d && (
          <dl className="tabla-auditoria">
            {filas.map(([k, v, nota]) => (
              <div key={k} className="fila-auditoria">
                <dt>{k}</dt>
                <dd>{v}{nota && <small>{nota}</small>}</dd>
              </div>
            ))}
          </dl>
        )}
        {d && <AuditaEnCadena red={red} factory={d.factory} logica={d.logica} timelock={d.timelock} />}
        {red.factories.length > 1 && (
          <p className="detalle" style={{ marginTop: 10 }}>
            {t("Factories anteriores, todavía activas para sus vaults:")}{" "}
            {red.factories.filter((a) => a.toLowerCase() !== red.factory.toLowerCase()).map((a) => (
              <span key={a} style={{ marginRight: 10 }}><Dir red={red} a={a} codigo /></span>
            ))}
          </p>
        )}
      </div>
    </details>
  );
}

/** Rutas de cada explorador: Etherscan y sus clones usan anclas (#readContract), Blockscout pestañas. */
function rutas(red: RedKeel, a: Address) {
  const base = EXPLORADOR[red.clave];
  const bs = base.includes("blockscout");
  return {
    contrato: `${base}/address/${a}${bs ? "?tab=contract" : "#code"}`,
    leer: `${base}/address/${a}${bs ? "?tab=read_contract" : "#readContract"}`,
    eventos: `${base}/address/${a}${bs ? "?tab=logs" : "#events"}`,
    sourcify: `https://repo.sourcify.dev/${red.idCadena}/${a}`,
  };
}

/** Botones para auditar directamente en la cadena, sin pasar por nuestra interfaz. */
function AuditaEnCadena({ red, factory, logica, timelock }: { red: RedKeel; factory: Address; logica: Address; timelock: Address }) {
  const t = useT();
  const f = rutas(red, factory), l = rutas(red, logica), tl = rutas(red, timelock);
  const enlaces: Array<[string, string, string]> = [
    [t("Código de la factory"), f.contrato, t("el contrato que crea todas las piscinas, verificado")],
    [t("Leer la factory"), f.leer, t("consulta tú mismo allVaults, vaultsOf, comisión y tesorería")],
    [t("Todos los vaults creados"), f.eventos, t("eventos VaultCreated: cada piscina que ha nacido aquí")],
    [t("Código de la lógica"), l.contrato, t("lo que ejecuta cada reajuste")],
    [t("Cambios anunciados"), tl.eventos, t("eventos del timelock: todo cambio de gobierno queda aquí")],
    [t("Sourcify"), f.sourcify, t("el código, verificado de forma independiente")],
    ["Arkham", urlArkham(factory), t("los flujos de dinero, en todas las redes")],
  ];
  return (
    <div className="audita-cadena">
      <h4>{t("Audítalo directamente en la blockchain")}</h4>
      <div className="audita-botones">
        {enlaces.map(([k, url, nota]) => (
          <a key={k} className="audita-boton" href={url} target="_blank" rel="noreferrer" title={nota}>
            <strong>{k} ↗</strong><small>{nota}</small>
          </a>
        ))}
      </div>
    </div>
  );
}

export function Transparencia({ alVolver }: { alVolver: () => void }) {
  const t = useT();
  return (
    <section className="transparencia">
      <button className="btn fantasma" onClick={alVolver}>{t("← Volver")}</button>
      <h1>{t("Transparencia")}</h1>
      <p className="sub">
        {t("No te pedimos que confíes en nosotros: todo lo importante está en la cadena y aquí te explicamos cómo comprobarlo tú.")}
      </p>

      <div className="tarjeta aviso-estado">
        <h2>{t("Estado de auditoría")}</h2>
        <ul>
          <li>{t("El código fuente de los contratos se publica en el explorador de cada red, para que cualquiera pueda leerlo y comprobar que es exactamente el que corre. Abajo tienes el enlace directo al código de cada contrato.")}</li>
          <li>{t("Los contratos pasan una batería de pruebas automáticas y revisiones de seguridad internas antes de cada despliegue.")}</li>
          <li><strong>{t("Todavía no tienen una auditoría externa de una firma independiente.")}</strong> {t("Lo decimos claro para que decidas con toda la información.")}</li>
        </ul>
      </div>

      <div className="tarjeta">
        <h2>{t("Cómo funciona")}</h2>
        <ol className="pasos-auditoria">
          <li><strong>{t("Tu propio contrato.")}</strong> {t("Cada piscina es un vault independiente a tu nombre. Tu dinero nunca se mezcla con el de otros usuarios.")}</li>
          <li><strong>{t("La posición es del vault.")}</strong> {t("La liquidez se deposita en el DEX (Uniswap, PancakeSwap, Aerodrome o PRJX) y el NFT de la posición queda a nombre de tu vault.")}</li>
          <li><strong>{t("Un robot la mantiene en rango.")}</strong> {t("Cuando el precio sale del rango, el robot llama a la función de reajuste. Toda la lógica y sus límites viven en el contrato, no en el robot.")}</li>
          <li><strong>{t("Tú sales cuando quieras.")}</strong> {t("Retirar es una función que solo tú puedes llamar y que funciona aunque el robot esté apagado o la piscina en pausa.")}</li>
        </ol>
      </div>

      <div className="tarjeta">
        <h2>{t("Quién puede hacer qué")}</h2>
        <div className="permisos">
          <div>
            <h3>{t("Tú (dueño del vault)")}</h3>
            <ul>
              <li>{t("Depositar, retirar todo o parte, y cobrar tus ganancias.")}</li>
              <li>{t("Cambiar el rango, las protecciones y la estrategia.")}</li>
              <li>{t("Pausar el robot o sustituirlo por otra dirección.")}</li>
              <li>{t("Decidir si adoptas una lógica nueva. Nadie puede cambiarla por ti.")}</li>
            </ul>
          </div>
          <div>
            <h3>{t("El robot (operador)")}</h3>
            <ul>
              <li>{t("Solo puede reajustar el rango, cobrar comisiones y reinvertir el saldo libre.")}</li>
              <li className="no">{t("No puede retirar tu dinero ni enviarlo a ninguna otra dirección.")}</li>
              <li className="no">{t("No puede cambiar tus ajustes.")}</li>
              <li>{t("Sus reajustes los limita el contrato: oráculo de precio medio (TWAP), deslizamiento máximo, tiempo mínimo entre reajustes y cupo de reajustes.")}</li>
              <li>{t("Su gas se paga de la bolsa de gas que tú decides cargar. En el peor caso, si su llave se viera comprometida, lo máximo a su alcance es esa bolsa: el contrato la separa del capital, la reserva y la ganancia.")}</li>
            </ul>
          </div>
          <div>
            <h3>{t("Keellabs (gobierno)")}</h3>
            <ul>
              <li>{t("Publicar una lógica nueva para los vaults que se creen después. Los existentes solo cambian si su dueño lo acepta.")}</li>
              <li>{t("Ajustar la comisión, siempre por debajo del tope que fija el propio contrato, y la tesorería que la recibe. Esto sí aplica también a los vaults existentes.")}</li>
              <li>{t("Mientras el contrato termina de estabilizarse, los cambios de gobierno los firma una wallet fría (hardware) del equipo, sin retardo. Cuando esté estable se reactivará el timelock de 24 h, que obliga a anunciar cada cambio públicamente un día antes. Cada ficha de red de abajo muestra en vivo cuál de los dos está activo.")}</li>
              <li className="no">{t("No puede tocar el capital de ningún vault ni bloquear tu retiro.")}</li>
            </ul>
          </div>
        </div>
      </div>

      <div className="tarjeta">
        <h2>{t("Comisiones")}</h2>
        <ul>
          <li>{t("Comisión de rendimiento: un porcentaje de las comisiones que gana tu posición, nunca de tu capital. El contrato impone un tope máximo que nadie puede superar.")}</li>
          <li>{t("Gas de los reajustes: se descuenta de la bolsa de gas de tu vault, por el coste real de cada operación y nunca más de lo que hay en ella.")}</li>
          <li>{t("Conversión al retirar: solo si eliges recibirlo todo en el estable. Retirar en los dos tokens no tiene comisión.")}</li>
          <li>{t("Referidos: quien te invitó cobra una parte de NUESTRA comisión. A ti no te cuesta nada.")}</li>
        </ul>
        <p className="detalle">{t("Los valores exactos de cada red están abajo, leídos en vivo del contrato.")}</p>
      </div>

      <div className="tarjeta">
        <h2>{t("El robot (keeper)")}</h2>
        <p className="detalle">{t("Es un programa que vigila tus piscinas cada pocos minutos. Su código no es público, pero no hace falta fiarse de él: todo lo que puede hacer está limitado por el contrato, que sí es público.")}</p>
        <ul>
          <li>{t("Reajusta solo cuando el precio sale del rango y el precio medio (TWAP) lo confirma, para no reaccionar a picos momentáneos.")}</li>
          <li>{t("Si tu vault va en pérdida recuperable, espera a que el precio se aleje del borde antes de actuar, para no convertir esa pérdida en definitiva.")}</li>
          <li>{t("Antes de firmar vuelve a comprobar el precio, y si la operación fuera a fallar no la envía.")}</li>
          <li>{t("Aplaza los reajustes cuando el gas de la red está caro, para que no te cuesten más de lo razonable.")}</li>
          <li>{t("Si el robot se detiene, tu vault simplemente se queda quieto: sigue ganando comisiones mientras el precio esté en rango y puedes retirar o reajustar tú mismo.")}</li>
        </ul>
      </div>

      <div className="tarjeta">
        <h2>{t("Riesgos")}</h2>
        <ul>
          <li><strong>{t("Pérdida impermanente.")}</strong> {t("Proveer liquidez significa que, si el precio se mueve mucho, puedes acabar con menos valor que si hubieras mantenido los tokens.")}</li>
          <li><strong>{t("Riesgo de contrato.")}</strong> {t("Ningún código está libre de errores, ni el nuestro ni el de los DEX sobre los que funciona.")}</li>
          <li><strong>{t("Riesgo de mercado.")}</strong> {t("Tokens con poca liquidez pueden tener deslizamientos grandes o precios inestables.")}</li>
          <li><strong>{t("Riesgo de red.")}</strong> {t("Una red congestionada o caída puede retrasar los reajustes.")}</li>
        </ul>
      </div>

      <div className="tarjeta">
        <h2>{t("Compruébalo tú mismo")}</h2>
        <ol className="pasos-auditoria">
          <li>{t("Abre la ficha de tu red abajo y entra en la factory, la lógica y la implementación: en el explorador verás el código fuente verificado.")}</li>
          <li>{t("En la ficha de cualquiera de tus piscinas, abre «Auditar esta piscina»: verás tu vault, la posición, la lógica que usa y el robot asignado.")}</li>
          <li>{t("Comprueba en el explorador que el dueño de tu vault es tu wallet y que el NFT de la posición está a nombre del vault.")}</li>
          <li>{t("Mira en el contrato del timelock los eventos «Encolada»: ahí aparece cualquier cambio anunciado, con la fecha a partir de la cual se puede aplicar.")}</li>
          <li>{t("Sigue el dinero en Arkham: cada piscina y cada dirección de esta página tienen su enlace. Verás en todas las redes a la vez qué entra, qué sale y hacia dónde, incluida la tesorería que cobra nuestra comisión.")}</li>
        </ol>
      </div>

      <div className="tarjeta">
        <h2>{t("Ve directo a la blockchain")}</h2>
        <p className="detalle">{t("No dependas de esta página: cada red tiene su explorador público y puedes consultar los contratos con cualquier herramienta (explorador, Arkham, Dune o tu propio nodo). Estos son los puntos de entrada:")}</p>
        <div className="redes-cadena">
          {redesActivas().map((r) => (
            <a key={r.clave} className="red-cadena" href={rutas(r, r.factory).contrato} target="_blank" rel="noreferrer">
              <IconoRed clave={r.clave} size={20} />
              <span><strong>{r.etiqueta}</strong><small>chain id {r.idCadena} · {new URL(EXPLORADOR[r.clave]).host}</small></span>
              <span aria-hidden="true">↗</span>
            </a>
          ))}
        </div>
      </div>

      <h2 style={{ marginTop: 28 }}>{t("Contratos por red")}</h2>
      <p className="sub" style={{ marginTop: 4 }}>{t("Datos leídos en vivo de la cadena cada vez que abres esta página.")}</p>
      {redesActivas().map((r) => <FichaRed key={r.clave} red={r} />)}
    </section>
  );
}
